import { useEffect, useMemo, useState, type ReactNode } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id, Doc } from "@constructionautomation/backend/convex/_generated/dataModel";
import {
  ArrowLeft,
  Download,
  Eye,
  FileText,
  RefreshCw,
  Trash2,
  AlertCircle,
  Layers,
} from "lucide-react";
import { toast } from "sonner";
import { useRouteParam } from "@/lib/use-route-param";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { useProject } from "@/components/projects";
import { ActionLogProvider, useRecordActionLog } from "@/hooks/use-action-log-session";

import { PageHeader } from "@/components/layout";
import { JobStatusBadge } from "@/components/job-status-badge";
import { ProcessingSpinner } from "@/components/processing-spinner";
import { ConfidenceIndicator } from "@/components/confidence-indicator";
import { GroupManager } from "@/components/group-manager";
import { RowSelectionTable } from "@/components/row-selection-table";
import { TableEditorModal } from "@/components/table-editor-modal";
import type { SourcePdfRef } from "@/components/spec-pilot-source-pdf-panel";
import {
  getTableRowsPayload,
  type TableRowsPayload,
  type TableRowsPayloadRef,
} from "@/lib/table-rows-cache";
import { formatRelativeTime } from "@/lib/utils";
import {
  useHydratedTables,
} from "@/features/spec-pilot/job-detail/use-hydrated-tables";
import { useRowGroupActions } from "@/features/spec-pilot/job-detail/use-row-group-actions";
import { useSubmittalGeneration } from "@/features/spec-pilot/job-detail/use-submittal-generation";
import type {
  HydratedTable,
  RowGroup,
  TableMetadata,
} from "@/features/spec-pilot/job-detail/types";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { DeleteConfirmationDialog } from "@/components/delete-confirmation-dialog";

export const Route = createFileRoute("/specPilot/$jobId")({
  component: JobDetailPage,
});

// Type definitions
type Job = Doc<"specPilotSubmittals">;
type Submittal = {
  _id: Id<"specPilotGeneratedSubmittals">;
  _creationTime: number;
  submittalId: Id<"specPilotSubmittals">;
  generatedFileStorageId?: Id<"_storage">;
  generatedFileKey?: string;
  generatedAt: number;
  includedTableIds: Id<"specPilotExtractedTables">[];
  operationId?: Id<"operations">;
  editedAt?: number;
  hasEditedData: boolean;
};

function JobHeaderDescription({
  projectName,
  sourceDocument,
}: {
  projectName: string;
  sourceDocument?: string;
}) {
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <span>{projectName}</span>
      {sourceDocument ? (
        <span className="text-xs font-medium text-muted-foreground/80">
          Source: {sourceDocument}
        </span>
      ) : null}
    </span>
  );
}

export function JobDetailPage() {
  const jobId = useRouteParam("jobId");
  useProject(); // Ensure project context is available for child components
  const { data: jobDetails } = useQuery(convexQuery(api.specPilot.submittals.getWithDetails, {
    submittalId: jobId as Id<"specPilotSubmittals">,
  }));

  // Loading state
  if (jobDetails === undefined) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Loading..."
          breadcrumbs={[
            { label: "SpecPilot", href: "/specPilot" },
            { label: "Loading..." },
          ]}
        />
        <Card>
          <ProcessingSpinner message="Loading job details..." />
        </Card>
      </div>
    );
  }

  // Not found state
  if (jobDetails === null) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Job Not Found"
          breadcrumbs={[
            { label: "SpecPilot", href: "/specPilot" },
            { label: "Not Found" },
          ]}
        />
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Job not found</AlertTitle>
          <AlertDescription>
            The job you are looking for does not exist or has been deleted.
          </AlertDescription>
        </Alert>
        <Button asChild variant="outline">
          <Link to="/specPilot">
            <ArrowLeft className="size-4" />
            Back to SpecPilot
          </Link>
        </Button>
      </div>
    );
  }

  const { job, project: jobProject, tables, submittal, rowGroups } = jobDetails;
  const safeRowGroups = rowGroups ?? [];
  const projectName = jobProject?.name ?? "Unknown";

  let content: ReactNode = null;
  switch (job.status) {
    case "pending":
    case "processing":
      content = (
        <ProcessingState job={job} projectName={projectName} />
      );
      break;
    case "failed":
      content = <FailedState job={job} projectName={projectName} />;
      break;
    case "ready_for_review":
      content = (
        <ReviewState
          job={job}
          projectName={projectName}
          tables={tables}
          rowGroups={safeRowGroups}
        />
      );
      break;
    case "completed":
      content = (
        <CompletedState
          job={job}
          projectName={projectName}
          tables={tables}
          submittal={submittal}
          rowGroups={safeRowGroups}
        />
      );
      break;
    default:
      content = null;
  }

  return (
    <ActionLogProvider
      projectId={job.projectId}
      pilot="SpecPilot"
      targetKind="specPilotSubmittal"
      targetId={job._id}
    >
      {content}
    </ActionLogProvider>
  );
}

// -----------------------------------------------------------------------------
// Processing State
// -----------------------------------------------------------------------------

interface ProcessingStateProps {
  job: Job;
  projectName: string;
}

function ProcessingState({ job, projectName }: ProcessingStateProps) {
  return (
    <div className="space-y-6">
      <PageHeader
        title={job.name}
        description={
          <JobHeaderDescription
            projectName={projectName}
            sourceDocument={job.originalFilename}
          />
        }
        breadcrumbs={[
          { label: "SpecPilot", href: "/specPilot" },
          { label: job.name },
        ]}
        actions={<JobStatusBadge status={job.status} />}
      />

      <Card>
        <ProcessingSpinner message="Extracting tables from your PDF..." />
      </Card>

      <Button asChild variant="outline">
        <Link to="/specPilot">
          <ArrowLeft className="size-4" />
          Back to SpecPilot
        </Link>
      </Button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Failed State
// -----------------------------------------------------------------------------

interface FailedStateProps {
  job: Job;
  projectName: string;
}

function FailedState({ job, projectName }: FailedStateProps) {
  const navigate = useNavigate();
  const removeJob = useMutation(api.specPilot.submittals.remove);
  const [isDeleting, setIsDeleting] = useState(false);

  async function handleDelete() {
    setIsDeleting(true);
    try {
      await removeJob({ submittalId: job._id });
      toast.success("Job deleted successfully");
      navigate({ to: "/specPilot" });
    } catch (error) {
      toast.error("Failed to delete job");
      console.error(error);
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={job.name}
        description={
          <JobHeaderDescription
            projectName={projectName}
            sourceDocument={job.originalFilename}
          />
        }
        breadcrumbs={[
          { label: "SpecPilot", href: "/specPilot" },
          { label: job.name },
        ]}
        actions={<JobStatusBadge status={job.status} />}
      />

      <Alert variant="destructive">
        <AlertCircle className="size-4" />
        <AlertTitle>Extraction Failed</AlertTitle>
        <AlertDescription>
          {job.error || "An unknown error occurred during table extraction."}
        </AlertDescription>
      </Alert>

      <div className="flex flex-wrap gap-3">
        <Button asChild variant="outline">
          <Link to="/specPilot">
            <ArrowLeft className="size-4" />
            Back to SpecPilot
          </Link>
        </Button>

        <DeleteConfirmationDialog
          title="Delete this job?"
          description="This action cannot be undone. This will permanently delete the job and all associated data."
          onConfirm={handleDelete}
          disabled={isDeleting}
          confirmLabel={isDeleting ? "Deleting..." : "Delete"}
        >
          <Button variant="destructive" disabled={isDeleting}>
            <Trash2 className="size-4" />
            Delete Job
          </Button>
        </DeleteConfirmationDialog>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Review State (Ready for Review)
// -----------------------------------------------------------------------------

interface ReviewStateProps {
  job: Job;
  projectName: string;
  tables: TableMetadata[];
  rowGroups: RowGroup[];
}

function ReviewState({ job, projectName, tables, rowGroups }: ReviewStateProps) {
  const { hydratedTableById, hydratedTables } = useHydratedTables(tables, rowGroups);
  const recordActionLog = useRecordActionLog();
  const {
    selectedRows,
    groupedRowsLookup,
    handleSelectionChange,
    handleRowToggle,
    replaceSelectedRowsForTable,
    clearSelectedRows,
    handleCreateGroup,
    handleUpdateGroup,
    handleDeleteGroup,
  } = useRowGroupActions(job._id, rowGroups, recordActionLog);
  const { isGenerating, runGeneration } = useSubmittalGeneration(job._id);
  const { data: sourcePdf } = useQuery(
    convexQuery(api.specPilot.submittals.getSourcePdfUrl, {
      submittalId: job._id,
    })
  );
  const appNavigate = useAppNavigate();
  const [createGroupRequest, setCreateGroupRequest] = useState<{
    id: number;
    tableId: string;
  } | null>(null);
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);

  // Select all tables by default
  const selectedCount = tables.filter((t) => t.selectedForSubmittal).length;

  async function handleGenerateSubmittal(tableSelectionCount = selectedCount) {
    const success = await runGeneration({
      selectedCount: tableSelectionCount,
      successMessage: "Submittal generated successfully",
      errorMessage: "Failed to generate submittal",
      emptySelectionMessage: "Select at least one table before generating",
    });
    // Redirect to submittal view only on success
    if (success) {
      await recordActionLog();
      appNavigate({
        to: "/specPilot/$jobId/submittal",
        params: { jobId: job._id },
        title: `${job.name} - Submittal`,
      });
    }
    return success;
  }

  function handleCreateGroupFromTable(table: HydratedTable) {
    const eligibleRows = table.rows
      .map((_, rowIndex) => rowIndex)
      .filter((rowIndex) => !groupedRowsLookup.has(`${table._id}:${rowIndex}`));
    const preselectedRows = selectedRows.get(table._id);
    const rowsForMerge =
      preselectedRows && preselectedRows.size > 0
        ? [...preselectedRows].filter(
            (rowIndex) => !groupedRowsLookup.has(`${table._id}:${rowIndex}`)
          )
        : eligibleRows;

    if (rowsForMerge.length === 0) {
      toast.error("All rows in this table are already merged");
      return;
    }

    replaceSelectedRowsForTable(table._id, rowsForMerge);
    setCreateGroupRequest((previous) => ({
      id: (previous?.id ?? 0) + 1,
      tableId: table._id,
    }));
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={job.name}
        description={
          <JobHeaderDescription
            projectName={projectName}
            sourceDocument={job.originalFilename}
          />
        }
        breadcrumbs={[
          { label: "SpecPilot", href: "/specPilot" },
          { label: job.name },
        ]}
        actions={<JobStatusBadge status={job.status} />}
      />

      <GroupManager
        tables={hydratedTables}
        selectedRows={selectedRows}
        rowGroups={rowGroups}
        createGroupRequest={createGroupRequest}
        onCreateGroup={handleCreateGroup}
        onUpdateGroup={handleUpdateGroup}
        onDeleteGroup={handleDeleteGroup}
        onClearSelection={clearSelectedRows}
      />

      {/* Extracted Tables */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Extracted Tables ({tables.length})
          </h2>
        </div>

        {tables.length === 0 ? (
          <Card className="py-8">
            <CardContent className="text-center">
              <p className="text-muted-foreground">
                No tables were extracted from this PDF.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {tables.map((table, index) => (
              <ExtractedTableCard
                key={table._id}
                table={table}
                index={index}
                prefetchedTable={hydratedTableById.get(table._id)}
                selectedRows={selectedRows.get(table._id) ?? new Set<number>()}
                groupedRowsLookup={groupedRowsLookup}
                onRowToggle={handleRowToggle}
                onSelectionChange={handleSelectionChange}
                onCreateGroupFromTable={handleCreateGroupFromTable}
                sourcePdf={sourcePdf}
                sourceId={job._id}
              />
            ))}
          </div>
        )}
      </div>

      <Button asChild variant="outline">
        <Link to="/specPilot">
          <ArrowLeft className="size-4" />
          Back to SpecPilot
        </Link>
      </Button>

      {/* Sticky Generate Button */}
      <Button
        className="fixed bottom-6 right-6 z-50 shadow-lg"
        onClick={() => setIsGenerateDialogOpen(true)}
        disabled={isGenerating}
        size="lg"
      >
        {isGenerating ? (
          <>
            <span className="animate-pulse">Generating...</span>
          </>
        ) : (
          <>
            Generate Submittal
            {selectedCount > 0 && (
              <span className="ml-2 rounded-full bg-primary-foreground/20 px-2 py-0.5 text-xs">
                {selectedCount}
              </span>
            )}
          </>
        )}
      </Button>

      <GenerateSubmittalDialog
        open={isGenerateDialogOpen}
        onOpenChange={setIsGenerateDialogOpen}
        tables={tables}
        isGenerating={isGenerating}
        onSelectionChange={handleSelectionChange}
        onGenerate={handleGenerateSubmittal}
      />
    </div>
  );
}

interface GenerateSubmittalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tables: TableMetadata[];
  isGenerating: boolean;
  title?: string;
  description?: string;
  confirmLabel?: string;
  onSelectionChange: (
    tableId: Id<"specPilotExtractedTables">,
    selected: boolean
  ) => Promise<boolean> | boolean;
  onGenerate: (selectedCount: number) => Promise<boolean>;
}

function GenerateSubmittalDialog({
  open,
  onOpenChange,
  tables,
  isGenerating,
  title = "Select Tables for Submittal",
  description = "Choose which extracted tables to include before generating the submittal.",
  confirmLabel = "Generate Submittal",
  onSelectionChange,
  onGenerate,
}: GenerateSubmittalDialogProps) {
  const [selectedTableIds, setSelectedTableIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    setSelectedTableIds(
      new Set(tables.filter((table) => table.selectedForSubmittal).map((table) => table._id))
    );
  }, [open, tables]);

  const selectedCount = selectedTableIds.size;

  function handleTableToggle(tableId: string, selected: boolean) {
    setSelectedTableIds((previous) => {
      const next = new Set(previous);
      if (selected) {
        next.add(tableId);
      } else {
        next.delete(tableId);
      }
      return next;
    });
  }

  async function handleConfirm() {
    try {
      const updates = tables.filter(
        (table) => selectedTableIds.has(table._id) !== table.selectedForSubmittal
      );

      const selectionResults = await Promise.all(
        updates.map((table) =>
          onSelectionChange(
            table._id,
            selectedTableIds.has(table._id)
          )
        )
      );

      if (selectionResults.some((result) => result === false)) {
        toast.error("Failed to update table selection. Please try again.");
        return;
      }

      const success = await onGenerate(selectedCount);
      if (success) {
        onOpenChange(false);
      }
    } catch (error) {
      console.error("Failed to prepare/generate submittal:", error);
      toast.error("Failed to generate submittal. Please try again.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="max-h-[55vh] space-y-2 overflow-y-auto py-2">
          {tables.length === 0 ? (
            <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
              No extracted tables are available for this job.
            </p>
          ) : (
            tables.map((table, index) => {
              const tableName = table.name || `Table ${index + 1}`;
              return (
                <div
                  key={table._id}
                  className="flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors hover:bg-muted/50"
                >
                  <Checkbox
                    id={`generate-table-${table._id}`}
                    checked={selectedTableIds.has(table._id)}
                    onCheckedChange={(checked) => handleTableToggle(table._id, checked === true)}
                    aria-label={`Include ${tableName}`}
                  />
                  <label
                    htmlFor={`generate-table-${table._id}`}
                    className="flex-1 cursor-pointer space-y-1"
                  >
                    <span className="block text-sm font-medium">{tableName}</span>
                    <span className="block text-xs text-muted-foreground">
                      Page {table.pageNumber} • {table.rowCount} rows x {table.headerCount} columns
                    </span>
                  </label>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isGenerating}>
            Cancel
          </Button>
          <Button onClick={() => void handleConfirm()} disabled={isGenerating}>
            {isGenerating ? "Generating..." : `${confirmLabel} (${selectedCount})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Extracted Table Card
// -----------------------------------------------------------------------------

interface ExtractedTableCardProps {
  table: TableMetadata;
  index: number;
  prefetchedTable?: HydratedTable;
  showRowSelection?: boolean;
  selectedRows: Set<number>;
  groupedRowsLookup: Map<string, { groupId: string; groupName: string }>;
  onRowToggle: (tableId: string, rowIndex: number, selected: boolean) => void;
  onCreateGroupFromTable: (table: HydratedTable) => void;
  onSelectionChange: (
    tableId: Id<"specPilotExtractedTables">,
    selected: boolean
  ) => Promise<boolean> | boolean;
  sourcePdf?: SourcePdfRef | null;
  sourceId?: string;
}

function ExtractedTableCard({
  table,
  index,
  prefetchedTable,
  showRowSelection = true,
  selectedRows,
  groupedRowsLookup,
  onRowToggle,
  onCreateGroupFromTable,
  onSelectionChange,
  sourcePdf,
  sourceId,
}: ExtractedTableCardProps) {
  const tableName = table.name || `Table ${index + 1}`;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const shouldHydrate = isModalOpen || (showRowSelection && table.selectedForSubmittal);
  const { data: rowPayloadRef } = useQuery(
    convexQuery(
      api.specPilot.extractedTables.getRowsPayloadRef,
      shouldHydrate && !prefetchedTable ? { tableId: table._id } : "skip"
    )
  );
  const [payload, setPayload] = useState<TableRowsPayload | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (prefetchedTable) {
      setPayload({
        headers: prefetchedTable.headers,
        rows: prefetchedTable.rows,
      });
      return () => {
        cancelled = true;
      };
    }

    if (!rowPayloadRef) {
      if (!shouldHydrate) {
        setPayload(null);
      }
      return () => {
        cancelled = true;
      };
    }

    async function hydrate() {
      try {
        const nextPayload = await getTableRowsPayload(
          rowPayloadRef as TableRowsPayloadRef
        );
        if (!cancelled) {
          setPayload(nextPayload);
        }
      } catch (error) {
        console.error("Failed to hydrate table", error);
        if (!cancelled) {
          setPayload(null);
        }
      }
    }

    void hydrate();
    return () => {
      cancelled = true;
    };
  }, [prefetchedTable, rowPayloadRef, shouldHydrate]);

  const fullTable = useMemo(() => {
    if (prefetchedTable) return prefetchedTable;
    if (!payload) return null;
    return {
      ...table,
      headers: payload.headers,
      rows: payload.rows,
    };
  }, [prefetchedTable, payload, table]);
  const eligibleRowCount = fullTable
    ? fullTable.rows.filter(
        (_, rowIndex) => !groupedRowsLookup.has(`${table._id}:${rowIndex}`)
      ).length
    : 0;
  const canCreateGroupFromTable = Boolean(
    fullTable && table.selectedForSubmittal && eligibleRowCount > 0
  );

  return (
    <>
      <Card className="py-4">
        <CardHeader className="pb-2">
          <div className="flex items-start gap-4">
            <Checkbox
              id={`table-${table._id}`}
              checked={table.selectedForSubmittal}
              onCheckedChange={(checked) =>
                onSelectionChange(table._id, checked === true)
              }
              aria-label={`Select ${tableName} for submittal`}
            />
            <div className="flex-1 space-y-1">
              <label
                htmlFor={`table-${table._id}`}
                className="cursor-pointer font-medium leading-none"
              >
                {tableName}
              </label>
              <CardDescription className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span>Page {table.pageNumber}</span>
                <span>
                  {table.rowCount} rows x {table.headerCount} columns
                </span>
                {table.confidence !== undefined && (
                  <ConfidenceIndicator
                    confidence={table.confidence}
                    variant="badge"
                    showLabel={false}
                  />
                )}
              </CardDescription>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fullTable && onCreateGroupFromTable(fullTable)}
                disabled={!canCreateGroupFromTable}
                title={
                  !table.selectedForSubmittal
                    ? "Select this table for submittal before merging rows"
                    : eligibleRowCount === 0
                      ? "All rows in this table are already merged"
                      : "Create Merge Rows from selected or unmerged rows in this table"
                }
              >
                <Layers className="size-4" />
                Merge Rows
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsModalOpen(true)}
              >
                <Eye className="size-4" />
                View Full
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {showRowSelection && table.selectedForSubmittal && (
            fullTable ? (
              <RowSelectionTable
                tableId={table._id}
                headers={fullTable.headers}
                rows={fullTable.rows}
                selectedRows={selectedRows}
                groupedRowsLookup={groupedRowsLookup}
                onRowToggle={onRowToggle}
              />
            ) : (
              <p className="text-sm text-muted-foreground mt-3">
                Loading table rows...
              </p>
            )
          )}
        </CardContent>
      </Card>

      {fullTable && (
        <TableEditorModal
          table={fullTable}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          sourcePdf={sourcePdf}
          sourceId={sourceId}
        />
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
// Completed State
// -----------------------------------------------------------------------------

interface CompletedStateProps {
  job: Job;
  projectName: string;
  tables: TableMetadata[];
  submittal: Submittal | null;
  rowGroups: RowGroup[];
}

function CompletedState({
  job,
  projectName,
  tables,
  submittal,
  rowGroups,
}: CompletedStateProps) {
  const { hydratedTableById, hydratedTables } = useHydratedTables(tables, rowGroups);
  const recordActionLog = useRecordActionLog();
  const {
    selectedRows,
    groupedRowsLookup,
    handleSelectionChange,
    handleRowToggle,
    replaceSelectedRowsForTable,
    clearSelectedRows,
    handleCreateGroup,
    handleUpdateGroup,
    handleDeleteGroup,
  } = useRowGroupActions(job._id, rowGroups, recordActionLog);
  const { isGenerating, runGeneration } = useSubmittalGeneration(job._id);
  const appNavigate = useAppNavigate();
  const { data: sourcePdf } = useQuery(
    convexQuery(api.specPilot.submittals.getSourcePdfUrl, {
      submittalId: job._id,
    })
  );
  const [createGroupRequest, setCreateGroupRequest] = useState<{
    id: number;
    tableId: string;
  } | null>(null);
  const [isRegenerateDialogOpen, setIsRegenerateDialogOpen] = useState(false);
  const selectedCount = tables.filter((t) => t.selectedForSubmittal).length;

  function handleCreateGroupFromTable(table: HydratedTable) {
    const eligibleRows = table.rows
      .map((_, rowIndex) => rowIndex)
      .filter((rowIndex) => !groupedRowsLookup.has(`${table._id}:${rowIndex}`));
    const preselectedRows = selectedRows.get(table._id);
    const rowsForMerge =
      preselectedRows && preselectedRows.size > 0
        ? [...preselectedRows].filter(
            (rowIndex) => !groupedRowsLookup.has(`${table._id}:${rowIndex}`)
          )
        : eligibleRows;

    if (rowsForMerge.length === 0) {
      toast.error("All rows in this table are already merged");
      return;
    }

    replaceSelectedRowsForTable(table._id, rowsForMerge);
    setCreateGroupRequest((previous) => ({
      id: (previous?.id ?? 0) + 1,
      tableId: table._id,
    }));
  }

  async function handleRegenerateSubmittal(tableSelectionCount = selectedCount) {
    const success = await runGeneration({
      selectedCount: tableSelectionCount,
      successMessage: "Submittal regeneration started",
      errorMessage: "Failed to regenerate submittal",
      emptySelectionMessage: "Select at least one table before regenerating",
    });

    if (success) {
      await recordActionLog();
      appNavigate({
        to: "/specPilot/$jobId/submittal",
        params: { jobId: job._id },
        title: `${job.name} - Submittal`,
      });
    }
    return success;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={job.name}
        description={
          <JobHeaderDescription
            projectName={projectName}
            sourceDocument={job.originalFilename}
          />
        }
        breadcrumbs={[
          { label: "SpecPilot", href: "/specPilot" },
          { label: job.name },
        ]}
        actions={<JobStatusBadge status={job.status} />}
      />

      {/* Submittal Section */}
      {submittal && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="size-5" />
              Generated Submittal
            </CardTitle>
            <CardDescription className="flex flex-wrap items-center gap-2">
              Last generated: {formatRelativeTime(submittal.generatedAt)}
              {submittal.hasEditedData && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                  Edited version available
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button
              onClick={() =>
                appNavigate({
                  to: "/specPilot/$jobId/submittal",
                  params: { jobId: job._id },
                  title: `${job.name} - Submittal`,
                })
              }
            >
              <Eye className="size-4" />
              View Submittal
            </Button>
            <DownloadButton
              storageId={submittal.generatedFileStorageId}
              fileKey={submittal.generatedFileKey}
              filename={`${job.name}-submittal.csv`}
            />
            <Button
              variant="outline"
              disabled={isGenerating}
              onClick={() => setIsRegenerateDialogOpen(true)}
            >
              <RefreshCw className="size-4" />
              {isGenerating ? "Regenerating..." : "Regenerate Submittal"}
            </Button>
          </CardContent>
        </Card>
      )}

      <GenerateSubmittalDialog
        open={isRegenerateDialogOpen}
        onOpenChange={setIsRegenerateDialogOpen}
        tables={tables}
        isGenerating={isGenerating}
        title="Select Tables for Regeneration"
        description="Choose which extracted tables to include. Regenerating will replace the generated submittal and discard manual edits made in the submittal viewer."
        confirmLabel="Regenerate Submittal"
        onSelectionChange={handleSelectionChange}
        onGenerate={handleRegenerateSubmittal}
      />

      <GroupManager
        tables={hydratedTables}
        selectedRows={selectedRows}
        rowGroups={rowGroups}
        createGroupRequest={createGroupRequest}
        onCreateGroup={handleCreateGroup}
        onUpdateGroup={handleUpdateGroup}
        onDeleteGroup={handleDeleteGroup}
        onClearSelection={clearSelectedRows}
      />

      {/* Extracted Tables Section */}
      <Card className="py-4">
        <CardHeader>
          <div className="flex items-center gap-2">
            <span className="font-semibold">
              Extracted Tables ({tables.length})
            </span>
          </div>
        </CardHeader>
        <CardContent className="grid gap-4 pt-0">
          {tables.map((table, index) => (
            <ExtractedTableCard
              key={table._id}
              table={table}
              index={index}
              prefetchedTable={hydratedTableById.get(table._id)}
              selectedRows={selectedRows.get(table._id) ?? new Set<number>()}
              groupedRowsLookup={groupedRowsLookup}
              onRowToggle={handleRowToggle}
              onCreateGroupFromTable={handleCreateGroupFromTable}
              onSelectionChange={handleSelectionChange}
              sourcePdf={sourcePdf}
              sourceId={job._id}
            />
          ))}
        </CardContent>
      </Card>

      <Button asChild variant="outline">
        <Link to="/specPilot">
          <ArrowLeft className="size-4" />
          Back to SpecPilot
        </Link>
      </Button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Download Button
// -----------------------------------------------------------------------------

interface DownloadButtonProps {
  storageId?: Id<"_storage">;
  fileKey?: string;
  filename: string;
}

function DownloadButton({ storageId, fileKey, filename }: DownloadButtonProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const { data: storageUrl } = useQuery(
    convexQuery(
      api.specPilot.generatedSubmittals.getStorageUrl,
      storageId || fileKey ? { storageId, fileKey } : "skip"
    )
  );

  async function handleDownload() {
    if (!storageUrl) return;
    setIsDownloading(true);
    try {
      const response = await fetch(storageUrl);
      if (!response.ok) throw new Error("Failed to fetch file");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (error) {
      toast.error("Failed to download file");
      console.error(error);
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <Button variant="outline" onClick={handleDownload} disabled={isDownloading || !storageUrl}>
      <Download className="size-4" />
      {isDownloading ? "Downloading..." : "Download CSV"}
    </Button>
  );
}

/*
 * Usage Example:
 *
 * The job detail page is automatically rendered when navigating to /specPilot/:jobId.
 * The page handles all job states (processing, failed, ready_for_review, completed)
 * and displays the appropriate UI for each state.
 *
 * Accessibility Checklist:
 * - All interactive elements are keyboard accessible
 * - Checkboxes have proper aria-label attributes
 * - Status changes are announced via toast notifications
 * - Loading states have appropriate ARIA live regions
 * - Alert dialogs follow WAI-ARIA patterns
 */
